import React, { useCallback, useEffect, useRef, useState } from 'react'
import { IconPlay } from '../../icons/IconPlay/IconPlay'
import { IconVideoPause } from '../../icons/IconVideoPause/IconVideoPause'
import { IconCross } from '../../icons/IconCross/IconCross'
import {
    StyledTtsPlayerBar,
    StyledTtsPlayPauseButton,
    StyledTtsSpeedWrap,
    StyledTtsSpeedButton,
    StyledTtsSpeedDropdown,
    StyledTtsSpeedOption,
    StyledTtsTime,
    StyledTtsProgressTrack,
    StyledTtsProgressFill,
    StyledTtsCloseButton,
} from './TtsPlayer.styled'

const SPEED_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const

function formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    return `${m}:${s.toString().padStart(2, '0')}`
}

export interface TtsPlayerBarProps {
    isPlaying: boolean
    currentTime: number
    duration: number
    playbackRate: number
    onPlayPause: () => void
    onSeek: (timeSeconds: number) => void
    onPlaybackRateChange: (rate: number) => void
    onClose: () => void
    className?: string
}

const SPEED_DROPDOWN_ID = 'tts-speed-menu'

export const TtsPlayerBar: React.FC<TtsPlayerBarProps> = ({
    isPlaying,
    currentTime,
    duration,
    playbackRate,
    onPlayPause,
    onSeek,
    onPlaybackRateChange,
    onClose,
    className,
}) => {
    const [speedOpen, setSpeedOpen] = useState(false)
    const progressTrackRef = useRef<HTMLDivElement>(null)
    const speedButtonRef = useRef<HTMLButtonElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Focus first menuitem when opening; return focus to trigger when closing
    useEffect(() => {
        if (!speedOpen) return
        const first = dropdownRef.current?.querySelector<HTMLButtonElement>(
            '[role="menuitem"]',
        )
        first?.focus()
        return () => {
            speedButtonRef.current?.focus()
        }
    }, [speedOpen])

    const handleProgressClick = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            const track = progressTrackRef.current
            if (!track || duration <= 0) return
            const rect = track.getBoundingClientRect()
            const x = e.clientX - rect.left
            const pct = Math.max(0, Math.min(1, x / rect.width))
            onSeek(pct * duration)
        },
        [duration, onSeek],
    )

    const percent = duration > 0 ? (currentTime / duration) * 100 : 0

    const handleSpeedMenuKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const menu = dropdownRef.current
            if (!menu) return
            const items = Array.from(
                menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
            )
            if (items.length === 0) return

            const currentIndex = items.indexOf(
                document.activeElement as HTMLButtonElement,
            )
            const nextIndex =
                currentIndex < 0 ? 0 : (currentIndex + 1) % items.length
            const prevIndex =
                currentIndex <= 0
                    ? items.length - 1
                    : (currentIndex - 1) % items.length

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault()
                    items[nextIndex]?.focus()
                    break
                case 'ArrowUp':
                    e.preventDefault()
                    items[prevIndex]?.focus()
                    break
                case 'Home':
                    e.preventDefault()
                    items[0]?.focus()
                    break
                case 'End':
                    e.preventDefault()
                    items[items.length - 1]?.focus()
                    break
                case 'Escape':
                    e.preventDefault()
                    setSpeedOpen(false)
                    break
                default:
                    break
            }
        },
        [],
    )

    return (
        <StyledTtsPlayerBar className={className}>
            <StyledTtsPlayPauseButton
                type="button"
                onClick={onPlayPause}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                data-icon={isPlaying ? 'pause' : 'play'}
            >
                {isPlaying ? (
                    <IconVideoPause />
                ) : (
                    <IconPlay />
                )}
            </StyledTtsPlayPauseButton>

            <StyledTtsSpeedWrap>
                <StyledTtsSpeedButton
                    ref={speedButtonRef}
                    type="button"
                    onClick={() => setSpeedOpen((o) => !o)}
                    aria-label="Playback speed"
                    aria-expanded={speedOpen}
                    aria-haspopup="menu"
                    aria-controls={speedOpen ? SPEED_DROPDOWN_ID : undefined}
                >
                    {playbackRate}x
                </StyledTtsSpeedButton>
                {speedOpen && (
                    <>
                        <div
                            style={{ position: 'fixed', inset: 0, zIndex: 9 }}
                            aria-hidden
                            onClick={() => setSpeedOpen(false)}
                        />
                        <StyledTtsSpeedDropdown
                            ref={dropdownRef}
                            id={SPEED_DROPDOWN_ID}
                            role="menu"
                            aria-label="Playback speed"
                            onKeyDown={handleSpeedMenuKeyDown}
                        >
                            {SPEED_OPTIONS.map((rate) => (
                                <StyledTtsSpeedOption
                                    key={rate}
                                    type="button"
                                    role="menuitem"
                                    data-selected={playbackRate === rate}
                                    aria-current={
                                        playbackRate === rate
                                            ? 'true'
                                            : undefined
                                    }
                                    onClick={() => {
                                        onPlaybackRateChange(rate)
                                        setSpeedOpen(false)
                                    }}
                                >
                                    {rate}x
                                </StyledTtsSpeedOption>
                            ))}
                        </StyledTtsSpeedDropdown>
                    </>
                )}
            </StyledTtsSpeedWrap>

            <StyledTtsTime>
                {formatTime(currentTime)} / {formatTime(duration)}
            </StyledTtsTime>

            <StyledTtsProgressTrack
                ref={progressTrackRef}
                onClick={handleProgressClick}
                role="slider"
                aria-valuemin={0}
                aria-valuemax={duration}
                aria-valuenow={currentTime}
                aria-label="Seek"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (duration <= 0) return
                    const step = e.shiftKey ? 10 : 5
                    if (e.key === 'ArrowLeft') {
                        e.preventDefault()
                        onSeek(Math.max(0, currentTime - step))
                    } else if (e.key === 'ArrowRight') {
                        e.preventDefault()
                        onSeek(Math.min(duration, currentTime + step))
                    }
                }}
            >
                <StyledTtsProgressFill percent={percent} />
            </StyledTtsProgressTrack>

            <StyledTtsCloseButton
                type="button"
                onClick={onClose}
                aria-label="Close"
            >
                <IconCross label="Close" />
            </StyledTtsCloseButton>
        </StyledTtsPlayerBar>
    )
}

TtsPlayerBar.displayName = 'TtsPlayerBar'
